import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { FileText, IndianRupee, Download } from 'lucide-react';
import { toast } from 'sonner';

export default function ClientInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});

  useEffect(() => {
    api.get('/invoices').then(r => { setInvoices(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const downloadPdf = async (invoiceId) => {
    setDownloading(prev => ({ ...prev, [invoiceId]: true }));
    try {
      const res = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${invoiceId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded');
    } catch {
      toast.error('Download failed');
    }
    setDownloading(prev => ({ ...prev, [invoiceId]: false }));
  };

  const statusColor = (s) => {
    const map = { pending: 'bg-amber-100 text-amber-800', paid: 'bg-green-100 text-green-800', overdue: 'bg-red-100 text-red-800', refunded: 'bg-gray-100 text-gray-800' };
    return map[s] || 'bg-gray-100 text-gray-800';
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#6A3E28] border-t-transparent" /></div>;

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <h1 className="text-2xl md:text-3xl font-bold text-[#3D2418]" style={{ fontFamily: 'Manrope' }}>Invoices</h1>
      {invoices.length === 0 ? (
        <Card className="rounded-2xl border-[#E8DDD4] border-dashed bg-white"><CardContent className="p-8 text-center"><FileText className="w-10 h-10 text-[#C48C68] mx-auto mb-3" /><p className="text-sm text-[#7A6B5E]">No invoices yet</p></CardContent></Card>
      ) : (
        <div className="space-y-3">
          {invoices.map(inv => (
            <Card key={inv.invoice_id} className="rounded-2xl border-[#E8DDD4] bg-white hover:shadow-md transition-shadow" data-testid={`invoice-${inv.invoice_id}`}>
              <CardContent className="p-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 bg-[#F5EDE6] rounded-xl flex items-center justify-center">
                    <IndianRupee className="w-5 h-5 text-[#6A3E28]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#3D2418]">{inv.invoice_id}</p>
                    <p className="text-xs text-[#A09387]">{inv.created_at?.split('T')[0]}</p>
                    <div className="flex gap-2 mt-1">
                      {inv.items?.map((item, i) => (
                        <span key={i} className="text-xs bg-[#F5EDE6] px-2 py-0.5 rounded text-[#6A3E28]">{item.name}</span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-lg font-bold text-[#3D2418] flex items-center justify-end"><IndianRupee className="w-4 h-4" />{inv.total?.toFixed(2)}</p>
                    {inv.gst_applied && <p className="text-xs text-[#A09387]">incl. GST {inv.gst_rate}%</p>}
                    <span className={`text-xs font-medium px-3 py-1 rounded-full ${statusColor(inv.status)}`}>{inv.status}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-lg border-[#C48C68] text-[#6A3E28] hover:bg-[#F5EDE6]"
                    onClick={() => downloadPdf(inv.invoice_id)}
                    disabled={downloading[inv.invoice_id]}
                    data-testid={`download-invoice-${inv.invoice_id}`}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    {downloading[inv.invoice_id] ? 'Loading...' : 'PDF'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
